import { use, useMemo, useState } from 'react';
import { useUrl } from 'crossroad';
import { useArchaeologyByPassageData } from '@/data/archaeology/archaeology-by-passage-data-context';
import { ArchaeologyByBookDataContext } from '@/data/archaeology/archaeology-by-book-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollText, ArrowRight, BookOpen } from 'lucide-react';
import { ArchaeologicalFindingCard } from './archaeological-finding-card';
import { usePlacesByPassageData } from '@/data/places/places-by-passage-data-context';
import { usePlacesData } from '@/data/places/places-data-context';
import { getBestTransitionImageFromPlaces } from '@/data/places/place-images-fetch';
import { useHistoricalEventsByPassageData } from '@/data/historical-events/historical-events-by-passage-data-context';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import archaeologyIllustration from '@/assets/archaeology-background.png';
import { ContextHero } from '../context/ContextHero';
import { normalizeBookName } from '@/lib/utils/bible-utils';

interface Props {
    passageId: string;
}

export default function PassageArchaeologicalBackground({ passageId }: Props) {
    const [, setUrl] = useUrl();
    const { data: archaeologyByPassage, isLoading: archaeologyLoading } = useArchaeologyByPassageData(passageId);
    const archaeologyByBookContext = use(ArchaeologyByBookDataContext);
    const passageContext = use(PassageDataContext);
    const { context: historicalEventsByPassageContext } = useHistoricalEventsByPassageData();
    const { getPlacesByPassageByPassageId } = usePlacesByPassageData();
    const { getPlaceByName } = usePlacesData();
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [isBookLevelVisible, setIsBookLevelVisible] = useState(false);
    const isDarkMode = useDarkMode();

    // Get the best transition image (same logic as animation)
    const bestImage = useMemo(() => {
        const placesByPassage = getPlacesByPassageByPassageId(passageId);
        const result = getBestTransitionImageFromPlaces(
            placesByPassage,
            getPlaceByName,
            historicalEventsByPassageContext?.getHistoricalEventsByPassageByPassageId,
            passageContext.get
        );

        // Debug logging when no image is found (only log if we have placesByPassage data)
        if (!result && placesByPassage) {
            console.log('🖼️ Archaeology page: No image found', {
                passageId,
                placesByPassage: placesByPassage,
                entityCount: placesByPassage.entities?.length || 0,
                primaryPlace: placesByPassage.primaryPlace
            });
        }

        return result;
    }, [passageId, getPlacesByPassageByPassageId, getPlaceByName, historicalEventsByPassageContext, passageContext]);

    const passage = useMemo(() => {
        return passageContext.data?.items.find(p => p.id === passageId);
    }, [passageContext.data, passageId]);

    const archaeologyByBook = useMemo(() => {
        if (!passage || !archaeologyByBookContext) return null;
        return archaeologyByBookContext.getArchaeologyByBookByBook(passage.book);
    }, [archaeologyByBookContext, passage]);

    // Get ruins image URL if available (from best image across all places)
    const ruinsImageUrl = bestImage?.ruinsImageUrl || null;

    const toggleItemExpansion = (itemId: string) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    if (archaeologyLoading || !passageContext.data) {
        return <LoadingSkeleton />;
    }

    if (!archaeologyByPassage) {
        return <EmptyState />;
    }

    const { findings } = archaeologyByPassage;

    return (
        <div className="space-y-4">
            {/* Cinematic Hero Card */}
            <ContextHero
                eyebrow="ARCHAEOLOGICAL CONTEXT"
                headline="Excavated Records"
                subtitle="Explore physical evidence, artifacts, and ruins that ground this passage in the historical world."
                image={ruinsImageUrl || archaeologyIllustration}
                metadata={ruinsImageUrl ? { place: bestImage?.placeName } : undefined}
                onClose={() => setUrl(`/app/passage/${passageId}`)}
                showClose={false}
                className="mb-8"
            />

            <div className="px-4">


                {/* Section Label */}
                {findings?.length > 0 && (
                    <div className="flex items-center gap-4 mb-8 px-1">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">{findings.length} Artifacts or Sites</span>
                                <div className="h-px w-8 bg-primary/20" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Archaeological Findings - Passage Level */}
                {findings?.length > 0 && (
                    <div className="space-y-4 relative">
                        {/* Subtle background image */}
                        <div className="absolute inset-0 -mx-4 sm:-mx-6 md:-mx-8 overflow-hidden rounded-2xl pointer-events-none">
                            <div
                                className="absolute inset-0 opacity-[0.25] bg-gradient-to-l from-transparent via-transparent to-background"
                                style={{
                                    backgroundImage: `url(${archaeologyIllustration})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'right center',
                                    maskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
                                    WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
                                }}
                            />
                        </div>

                        <div className="relative z-10 space-y-4">
                            {findings.map((finding, index) => {
                                const itemId = `passage-finding-${index}`;
                                const isExpanded = expandedItems.has(itemId);

                                return (
                                    <ArchaeologicalFindingCard
                                        key={index}
                                        finding={finding}
                                        bookName={passage?.book}
                                        isExpanded={isExpanded}
                                        onToggleExpansion={() => toggleItemExpansion(itemId)}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Book-Level Transition & Section */}
                {archaeologyByBook && (
                    <div className="pt-10 px-1">
                        {/* The Cinematic Transition Banner */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            className={cn(
                                "relative overflow-hidden p-8 md:p-12 rounded-[2.5rem] border transition-all duration-500",
                                isDarkMode
                                    ? "bg-slate-900/60 border-slate-800/60 shadow-[inset_0_0_80px_rgba(0,0,0,0.3)]"
                                    : "bg-slate-50 border-slate-200/60 shadow-inner"
                            )}
                        >
                            {/* Background Reconstruction Image */}
                            {ruinsImageUrl && (
                                <div className="absolute inset-0 z-0 opacity-20 saturate-[0.8] grayscale-[0.2]">
                                    <img
                                        src={ruinsImageUrl}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/40 to-transparent" />
                                </div>
                            )}

                            {/* Aesthetic BG element */}
                            <div className="absolute top-0 right-0 p-8 opacity-5 z-10">
                                <BookOpen className="w-32 h-32" />
                            </div>
                            <div className="absolute -left-12 -bottom-12 w-64 h-64 bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

                            <div className="relative max-w-2xl space-y-6 z-10">
                                <div className="space-y-2">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-primary">The Wider Horizon</span>
                                        <div className="h-px flex-1 bg-primary/20" />
                                    </div>
                                    <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight">
                                        {normalizeBookName(passage?.book)} Findings
                                    </h3>
                                </div>

                                <p className="text-base md:text-lg leading-relaxed text-muted-foreground/90 italic font-medium">
                                    Explore the broader archaeological scope that defines the world of {normalizeBookName(passage?.book)}.
                                </p>

                                <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                    <Button
                                        size="lg"
                                        onClick={() => setIsBookLevelVisible(!isBookLevelVisible)}
                                        className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-[11px] shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
                                    >
                                        {isBookLevelVisible ? "Collapse Book Context" : `Explore ${normalizeBookName(passage?.book)} Record`}
                                        <ArrowRight className={cn("ml-2 h-4 w-4 transition-transform", isBookLevelVisible && "rotate-90")} />
                                    </Button>

                                    <div className="flex items-center gap-2 text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">
                                        <ScrollText className="h-3.5 w-3.5" />
                                        <span>{archaeologyByBook.findings?.length || 0} Findings</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Book-Level Findings List */}
                        <AnimatePresence>
                            {isBookLevelVisible && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.5, ease: "easeInOut" }}
                                    className="overflow-hidden"
                                >
                                    <div className="space-y-12 pt-16">
                                        <div className="max-w-3xl space-y-4 px-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-1.5 h-1.5 rounded-full bg-primary/40" />
                                                <h4 className="text-[11px] font-bold uppercase tracking-[0.3em] text-muted-foreground">The Summary</h4>
                                            </div>
                                            <p className="text-lg leading-relaxed text-foreground/80 italic border-l-2 border-primary/20 pl-6">
                                                {archaeologyByBook.summary}
                                            </p>
                                        </div>

                                        {archaeologyByBook.findings && archaeologyByBook.findings.length > 0 && (
                                            <div className="space-y-6">
                                                {archaeologyByBook.findings.map((finding, index) => {
                                                    const itemId = `book-finding-${index}`;
                                                    const isExpanded = expandedItems.has(itemId);

                                                    return (
                                                        <ArchaeologicalFindingCard
                                                            key={index}
                                                            finding={finding}
                                                            bookName={passage?.book}
                                                            isExpanded={isExpanded}
                                                            onToggleExpansion={() => toggleItemExpansion(itemId)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                )}

                {/* AI Disclaimer at the bottom */}
                <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">
                    <AIDisclaimerBadge
                        model={archaeologyByPassage?.generatedBy || (archaeologyByPassage as any)?.source}
                        passageId={passageId}
                        contextType="archaeological-context"
                    />
                    <p className="text-[10px] text-muted-foreground text-center max-w-[400px]">
                        This archaeological record is generated by the BibleGlen Archaeological Module, utilizing Gemini 3.0 Pro to synthesize site data. Always consult primary site reports and peer-reviewed journals for academic purposes.
                    </p>
                </div>
            </div>
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-64" />
                </CardHeader>
                <CardContent className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-4 w-full" />
                </CardContent>
            </Card>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="flex flex-col">
            <p className="text-sm text-muted-foreground max-w-md">
                No archaeological context was found for this specific passage.
            </p>
        </div>
    );
}
